import React from 'react';
import PropTypes from 'prop-types';
import styles from './Badge.module.scss';

/* ────────────────────────────────────────────────────────────
 * Badge — soft-tinted pill for status / availability / category.
 *
 *   <Badge tone="success">Active</Badge>
 *
 * Prop unification (PROMPT_TEMPLATE.md §4 d2):
 *   Badge accepts both `tone` (the unified color-prop name —
 *   matches Modal sub-primitives + IconButton) and the original
 *   `variant`. `tone` wins when both are set. New code uses
 *   `tone`; the `variant` alias is kept for ~50+ existing
 *   callers and will be removed in a future cleanup PR.
 *
 * Clickable pill (`onClick`):
 *   A status that opens something — the Order Listing's
 *   "540 m off" opens the review of that delivery. With
 *   `onClick` the pill renders as a real <button type="button">
 *   (keyboard, focus ring, hover outline) in the same tone and
 *   size; without it, the exact <span> it always was. Same
 *   `leftIcon` / `rightIcon` slots as <Button>:
 *
 *     <Badge tone="warning" onClick={open}
 *            leftIcon={<TriangleAlert size={14} />}>540 m off</Badge>
 * ──────────────────────────────────────────────────────────── */

const TONE_VALUES = ['neutral', 'success', 'warning', 'danger', 'info'];

const Badge = ({
  tone,
  variant,
  size,
  leftIcon,
  rightIcon,
  onClick,
  children,
  className,
}) => {
  const colorTone = tone || variant;
  const hasIcon = Boolean(leftIcon || rightIcon);

  const classNames = [
    styles.badge,
    styles[colorTone],
    styles[size],
    hasIcon ? styles.withIcon : '',
    onClick ? styles.interactive : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  const content = hasIcon ? (
    <>
      {leftIcon && (
        <span className={styles.icon} aria-hidden="true">
          {leftIcon}
        </span>
      )}
      <span>{children}</span>
      {rightIcon && (
        <span className={styles.icon} aria-hidden="true">
          {rightIcon}
        </span>
      )}
    </>
  ) : (
    children
  );

  if (onClick) {
    return (
      <button type="button" className={classNames} onClick={onClick}>
        {content}
      </button>
    );
  }
  return <span className={classNames}>{content}</span>;
};

Badge.propTypes = {
  // `tone` — unified color-prop name (preferred for new code).
  tone: PropTypes.oneOf(TONE_VALUES),
  // `variant` — original prop name, still supported.
  variant: PropTypes.oneOf(TONE_VALUES),
  size: PropTypes.oneOf(['sm', 'md']),
  // Decorative icons either side of the text (hidden from screen readers —
  // the text is the label).
  leftIcon: PropTypes.node,
  rightIcon: PropTypes.node,
  // Makes the pill a button. Omitted, the Badge is plain text as before.
  onClick: PropTypes.func,
  children: PropTypes.node,
  className: PropTypes.string,
};

Badge.defaultProps = {
  tone: undefined,
  variant: 'neutral',
  size: 'md',
  leftIcon: null,
  rightIcon: null,
  onClick: undefined,
  children: null,
  className: '',
};

export default Badge;
